import type { OnChangeFn, SortingState } from "@tanstack/react-table"
import { useCallback, useMemo, useState } from "react"
import { useQuery } from "urql"

import { CUSTOMER_ORDER_DATE_RANGE_TYPE_TO_SALES_ORDER_DATE_RANGE_TYPE } from "../constants"
import {
  filterSalesOrdersByQuery,
  sortSalesOrdersByDeliveryDate,
  sortSalesOrdersByShippedDate,
} from "../salesOrderPreview"

import "../graphql/salesOrderQueries"
import {
  type CustomerOrderDateRangeType,
  GetCustomerOrderSalesOrdersDocument,
  type GetCustomerOrderSalesOrdersQuery,
  type SalesOrder,
  SalesOrderSortField,
  type SalesOrderStatus,
  SortingOrder,
} from "@/graphql/codegen/graphql"
import { DEFAULT_PAGE_SIZE } from "@/stores/createSearchStore"

interface UseSalesOrderPreviewParams {
  companyId: string
  shipToLocationId: string
  query?: string
  statuses?: SalesOrderStatus[]
  dateFrom?: string
  dateTo?: string
  dateRangeType?: CustomerOrderDateRangeType
}

interface UseSalesOrderPreviewReturn {
  salesOrders: SalesOrder[]
  isFetching: boolean
  errorMessage?: string
  sorting: SortingState
  onSortingChange: OnChangeFn<SortingState>
}

export const useSalesOrderPreview = ({
  companyId,
  shipToLocationId,
  query,
  statuses,
  dateFrom,
  dateTo,
  dateRangeType,
}: UseSalesOrderPreviewParams): UseSalesOrderPreviewReturn => {
  const [sorting, setSorting] = useState<SortingState>([])

  const [{ data, fetching, error }] = useQuery<GetCustomerOrderSalesOrdersQuery>({
    query: GetCustomerOrderSalesOrdersDocument,
    variables: {
      input: {
        filter: {
          companyId,
          shipToLocationId,
          statuses,
          dateFrom,
          dateTo,
          dateRangeType: dateRangeType
            ? CUSTOMER_ORDER_DATE_RANGE_TYPE_TO_SALES_ORDER_DATE_RANGE_TYPE[dateRangeType]
            : undefined,
        },
        sort: { field: SalesOrderSortField.DeliverDate, order: SortingOrder.Asc },
        offset: 0,
        limit: DEFAULT_PAGE_SIZE,
      },
    },
  })

  const output = data?.salesOrder.list
  const rawSalesOrders = useMemo<SalesOrder[]>(
    () => (output?.__typename === "SalesOrderListSuccess" ? (output.salesOrders as SalesOrder[]) : []),
    [output]
  )
  const errorMessage = output?.__typename === "SalesOrderListFailure" ? output.error.message : error?.message

  const activeSort = sorting[0]

  const salesOrders = useMemo(() => {
    // AK-6776(b): SalesOrderListFilter has no query field yet, so the top-level search term only
    // narrows the sales orders already fetched for this row, not the full dataset.
    const filtered = filterSalesOrdersByQuery(rawSalesOrders, query)

    if (!activeSort) return filtered

    const order = activeSort.desc ? SortingOrder.Desc : SortingOrder.Asc

    if (activeSort.id === "warehouseShippedOn") return sortSalesOrdersByShippedDate(filtered, order)
    if (activeSort.id === "deliveryDate") return sortSalesOrdersByDeliveryDate(filtered, order)

    return filtered
  }, [rawSalesOrders, query, activeSort])

  const onSortingChange: OnChangeFn<SortingState> = useCallback((updater) => {
    setSorting((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater
      return next.length ? next : prev
    })
  }, [])

  return {
    salesOrders,
    isFetching: fetching,
    errorMessage,
    sorting,
    onSortingChange,
  }
}
